import React, { useEffect, useState } from 'react';
import cx from 'classnames';

import { Button } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import { LoadingOutlined } from '@ant-design/icons';
import { StripePaymentElementOptions } from '@stripe/stripe-js';

import { showErrorMessage } from '../../../../slices/notifications';
import { confirmUpgradeToProfessionalPayment } from '../../../../slices/upgradeAccount/upgradeToPro.thunk';
import { selectConfirmingPaymentProState } from '../../../../slices/upgradeAccount/upgradeToPro.selectors';
import { Features, isFeatureEnabled } from '../../../../featureToggles/features';

import styles from './upgradeCreditCardForm.module.scss';

interface ICreditCardFormProps {
  backAction: () => void;
  nextAction: () => void;
}

export const CreditCardForm = ({ backAction, nextAction }: ICreditCardFormProps): JSX.Element => {
  const [isLoading, setIsLoading] = useState(false);
  const [stripePaymentCompleted, setStripePaymentCompleted] = useState(false);
  const stripe = useStripe();
  const elements = useElements();
  const isConfirmingPayment = useSelector(selectConfirmingPaymentProState);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!isConfirmingPayment && stripePaymentCompleted) {
      nextAction();
    }
  }, [isConfirmingPayment, stripePaymentCompleted, nextAction]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }
    setIsLoading(true);
    const result = await stripe.confirmPayment({
      elements,
      confirmParams: {},
      redirect: 'if_required'
    });

    if (result.error) {
      dispatch(showErrorMessage(result.error.message ?? 'Unexpected error'));
      setIsLoading(false);
    } else {
      dispatch(confirmUpgradeToProfessionalPayment());
      setStripePaymentCompleted(true);
    }
  };

  if (!stripe || !elements) {
    return <LoadingOutlined />;
  }

  const options: StripePaymentElementOptions = {
    terms: {
      card: 'never'
    },
    wallets: {
      applePay: 'never',
      googlePay: 'never'
    }
  };

  const isSubmitDisabled = !stripe || isConfirmingPayment;
  return (
    <form onSubmit={handleSubmit}>
      <section className={styles.content}>
        <PaymentElement options={options}/>
      </section>
      <footer className={styles.footer}>
        <div className={cx(styles.actions, { [styles.tiers]: isFeatureEnabled(Features.TrialsOfTiers) })}>
          <Button
            type="link"
            onClick={backAction}
            disabled={isLoading}
            className={styles.backButton}
          >
            <span>Back</span>
          </Button>
          <Button
            htmlType="submit"
            type="primary"
            className={styles.payButton}
            disabled={isSubmitDisabled || isLoading}
          >
            <span>Pay now</span>
          </Button>
        </div>
      </footer>
    </form>
  );
};
